import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAuth } from "../../context/AuthContext";
import axiosClient from "../../api/axiosClient";
import ConfirmDeleteModal from "../Modal/ConfirmDeleteModal";
import UserSettingsShell from "./UserSettingsShell";
import UserSettingsSidebar from "./UserSettingsSidebar";
import BillingProfileSettingsModal from "./modals/BillingProfileSettingsModal";
import BillingInvoicesModal from "./modals/BillingInvoicesModal";
import UserSettingsNotificationsTab from "./tabs/UserSettingsNotificationsTab";
import UserSettingsBillingTab from "./tabs/UserSettingsBillingTab";
import UserSettingsPaymentsTab from "./tabs/UserSettingsPaymentsTab";
import UserSettingsAccountTab from "./tabs/UserSettingsAccountTab";
import useNotificationPreferences from "./hooks/useNotificationPreferences";
import useBillingInvoices from "./hooks/useBillingInvoices";
import useSettingsReturnState from "./hooks/useSettingsReturnState";

const hasValidLocationCoordinates = (currentUser) => {
	const coords = currentUser?.locationCoordinates?.coordinates;
	return (
		Array.isArray(coords) &&
		coords.length === 2 &&
		coords.every((coord) => typeof coord === "number" && !Number.isNaN(coord))
	);
};

const getBillingProfileSummary = (user, t) => {
	if (!user?.billingProfileCompleted || !user?.legalName || !user?.taxId) {
		return t("settings.billing.profile.emptySummary");
	}

	return `${user.legalName} · ${user.taxId}`;
};

const getInterestsSummary = (user, t) => {
	const interestCount =
		(user?.eventTypeInterests?.length || 0) +
		(user?.terrainInterests?.length || 0) +
		(user?.experienceInterests?.length || 0) +
		(user?.vehicleTypeInterests?.length || 0);
	const hasAttendancePrefs = Boolean(user?.eventFrequency);
	const hasOrganizerPrefs = Boolean(
		user?.organizingFrequency || user?.professionalOrganizer,
	);
	const hasRecommendations = Boolean(user?.receiveEventSuggestions);
	const hasLocation = Boolean(user?.nearbyCity);

	if (
		interestCount === 0 &&
		!hasAttendancePrefs &&
		!hasOrganizerPrefs &&
		!hasRecommendations &&
		!hasLocation
	) {
		return t("settings.account.interests.emptySummary");
	}

	if (hasRecommendations && hasLocation) {
		if (interestCount > 0) {
			return t("settings.account.interests.summaryWithLocation", {
				count: interestCount,
				location: user.nearbyCity,
			});
		}

		return t("settings.account.interests.summaryLocationOnly", {
			location: user.nearbyCity,
		});
	}

	if (interestCount > 0) {
		return t("settings.account.interests.summaryCount", {
			count: interestCount,
		});
	}

	if (hasAttendancePrefs || hasOrganizerPrefs) {
		return t("settings.account.interests.summaryPreferences");
	}

	if (hasRecommendations) {
		return t("settings.account.interests.summaryRecommendations");
	}

	return t("settings.account.interests.emptySummary");
};

function UserSettingsContent({ onClose }) {
	const { t, i18n } = useTranslation("userProfile");
	const { userId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { user, setUser, logout, stripeStatus, refreshStripeStatus } =
		useAuth();
	const currentUserId = user?.id || user?._id || null;

	const [activeTab, setActiveTab] = useState("notifications");
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isBillingProfileModalOpen, setIsBillingProfileModalOpen] =
		useState(false);
	const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false);
	const [isDeletingAccount, setIsDeletingAccount] = useState(false);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const onboardingStatus = params.get("onboarding");

		if (onboardingStatus === "success") {
			toast.success(
				"Onboarding de Stripe completado (o se ha detectado como finalizado).",
			);
		} else if (onboardingStatus === "cancelled") {
			toast.warn("Onboarding de Stripe cancelado o no finalizado.");
		}
	}, []);

	useEffect(() => {
		if (!currentUserId) {
			return;
		}

		if (!userId) {
			navigate(`/user/${currentUserId}/settings`, { replace: true });
			return;
		}

		if (currentUserId !== userId) {
			navigate(`/user/${currentUserId}/settings`, { replace: true });
		}
	}, [currentUserId, navigate, userId]);

	const { goToEditProfile, goToEditInterests, goToLocationSettings } =
		useSettingsReturnState({
			currentUserId,
			locationState: location.state,
			navigate,
		});

	const { prefs, savingOptionKeys, handleSectionAutosave } =
		useNotificationPreferences({
			user,
			setUser,
			t,
		});

	const {
		billingInvoices,
		billingInvoicesTotal,
		billingInvoicesHasMore,
		billingInvoicesLoaded,
		loadingInvoiceId,
		handleInvoiceDownload,
		getInvoicesSummary,
		formatInvoiceAmount,
		formatInvoiceDate,
		getInvoiceStatusLabel,
	} = useBillingInvoices({
		currentUserId,
		userId,
		t,
		language: i18n.language,
	});

	const closeBillingProfileModal = useCallback(() => {
		setIsBillingProfileModalOpen(false);
	}, []);

	const openBillingProfileModal = useCallback(() => {
		setIsBillingProfileModalOpen(true);
	}, []);

	const closeInvoicesModal = useCallback(() => {
		setIsInvoicesModalOpen(false);
	}, []);

	const openInvoicesModal = useCallback(() => {
		setIsInvoicesModalOpen(true);
	}, []);

	const openDeleteModal = useCallback(() => {
		setShowDeleteModal(true);
	}, []);

	const closeDeleteModal = useCallback(() => {
		setShowDeleteModal(false);
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		try {
			setIsDeletingAccount(true);
			const response = await axiosClient.delete("/api/users/delete");
			if (!response.data?.success) {
				throw new Error("Delete account request failed");
			}

			toast.success(t("settings.account.deleteModal.success"));
			closeDeleteModal();
			await logout();
		} catch (error) {
			console.error("Error deleting account:", error);
			toast.error(t("settings.account.deleteModal.error"));
		} finally {
			setIsDeletingAccount(false);
		}
	}, [closeDeleteModal, logout, t]);

	const billingProfileSummary = useMemo(
		() => getBillingProfileSummary(user, t),
		[user, t],
	);

	const interestsSummary = useMemo(
		() => getInterestsSummary(user, t),
		[user, t],
	);

	const activeTabContent = {
		notifications: (
			<UserSettingsNotificationsTab
				t={t}
				prefs={prefs}
				savingOptionKeys={savingOptionKeys}
				onSectionAutosave={handleSectionAutosave}
				hasLocationCoordinates={hasValidLocationCoordinates(user)}
				onConfigureLocation={goToLocationSettings}
			/>
		),
		billing: (
			<UserSettingsBillingTab
				t={t}
				user={user}
				billingInvoicesLoaded={billingInvoicesLoaded}
				billingInvoicesTotal={billingInvoicesTotal}
				getBillingProfileSummary={() => billingProfileSummary}
				getInvoicesSummary={getInvoicesSummary}
				onOpenBillingProfile={openBillingProfileModal}
				onOpenInvoices={openInvoicesModal}
			/>
		),
		payments: (
			<UserSettingsPaymentsTab
				t={t}
				user={user}
				stripeStatus={stripeStatus}
				refreshStripeStatus={refreshStripeStatus}
				setUser={setUser}
			/>
		),
		account: (
			<UserSettingsAccountTab
				t={t}
				user={user}
				getInterestsSummary={() => interestsSummary}
				onEditProfile={goToEditProfile}
				onEditInterests={goToEditInterests}
				onDeleteAccount={openDeleteModal}
			/>
		),
	};

	return (
		<>
			<UserSettingsShell
				sidebar={
					<UserSettingsSidebar
						title={t("settings.title")}
						closeLabel={t("settings.close")}
						activeTab={activeTab}
						onTabChange={setActiveTab}
						onClose={onClose}
					/>
				}
			>
				<SettingsTabPanel
					id={`settings-panel-${activeTab}`}
					role="tabpanel"
					aria-labelledby={`settings-tab-${activeTab}`}
				>
					{activeTabContent[activeTab] || activeTabContent.notifications}
				</SettingsTabPanel>
			</UserSettingsShell>

			<BillingProfileSettingsModal
				isOpen={isBillingProfileModalOpen}
				onClose={closeBillingProfileModal}
				title={t("settings.billing.profile.title")}
				closeLabel={t("settings.close")}
				user={user}
				onSuccess={closeBillingProfileModal}
				submitLabel={t("settings.billing.profile.save")}
				modalTitle={t("settings.billing.profile.modalTitle")}
				modalDescription={t("settings.billing.profile.modalDescription")}
			/>
			<BillingInvoicesModal
				isOpen={isInvoicesModalOpen}
				onClose={closeInvoicesModal}
				closeLabel={t("settings.close")}
				t={t}
				billingInvoices={billingInvoices}
				billingInvoicesHasMore={billingInvoicesHasMore}
				billingInvoicesTotal={billingInvoicesTotal}
				loadingInvoiceId={loadingInvoiceId}
				onDownloadInvoice={handleInvoiceDownload}
				formatInvoiceDate={formatInvoiceDate}
				formatInvoiceAmount={formatInvoiceAmount}
				getInvoiceStatusLabel={getInvoiceStatusLabel}
			/>

			{showDeleteModal && (
				<ConfirmDeleteModal
					onClose={closeDeleteModal}
					onConfirm={handleConfirmDelete}
					isLoading={isDeletingAccount}
				/>
			)}
		</>
	);
}

export default UserSettingsContent;

const SettingsTabPanel = styled.div`
	flex: 1;
	min-width: 0;
	width: 100%;
`;
